import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Form, Input, Tab, TextArea } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToasts } from 'common/toast';
import PageTitle, { Container } from 'common/layouts/AuthLayout/PageTitle';
import { RecommendationType } from '../../../../common/constants/RecommendationType';
import { templatesApi } from '../api/recommendation-templates';
import { PhaseTab } from '../PhaseTab';
import { ResourceModal } from '../ResourceModal';
import cloneDeep from 'lodash/cloneDeep';
import LoadingSpinner from 'common/components/LoadingSpinner';

const newEntityState = () => ({
  title: '',
  description: '',
  phases: [{ activities: [] }, { activities: [] }, { activities: [] }],
  associations: {
    factor: null,
    organizationalGoal: null,
  },
  context: {
    whyRecommendation: '',
    focusAreas: '',
    recommendationQuestion: '',
    organizationQuestions: [],
    effortRequires: [],
    involvedAreas: '',
    desiredOutcome: '',
    timeframe: '',
    cost: '',
    policyChange: '',
    cultureChange: '',
    dataCollection: '',
    communityConnections: '',
    primaryStakeholder: '',
  },
});

const Foundation = () => {
  const navigate = useNavigate();
  const { id: templateId } = useParams();
  const { addToast } = useToasts();

  const [modalOpen, setModalOpen] = useState(false);
  const [currentResources, setCurrentResources] = useState<any[]>([]);
  const [currentactivityIndex, setCurrentactivityIndex] = useState<number | null>(null);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState<number | null>(null);
  const [currentTaskIndex, setCurrentTaskIndex] = useState<number | null>(null);

  const [foundationProject, setFoundationProject] = useState<
    | {
        title: string;
        description: string;
        phases: any[];
        associations: { organizationalGoal: any; factor: any };
        context: any;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (!templateId) {
      setFoundationProject(newEntityState());
    }
  }, [templateId]);

  const { data: existingProject, isLoading: projectIsLoading } =
    templatesApi.useApiAdminV1RecommendationTemplateRetrieveQuery(
      {
        id: Number(templateId),
      },
      { skip: !templateId }
    );

  const [triggerUpdate] = templatesApi.useApiAdminV1RecommendationTemplateUpdateMutation();

  const [triggerInsert] = templatesApi.useApiAdminV1RecommendationTemplateCreateMutation();

  useEffect(() => {
    if (existingProject) {
      setFoundationProject({
        title: existingProject.name,
        ...cloneDeep(existingProject.template),
      });
    }
  }, [existingProject]);

  const saveFoundation = () => {
    if (!foundationProject) {
      return;
    }
    const updateId = templateId || null;

    const templatePayload = {
      id: updateId || null,
      name: foundationProject.title,
      status: 9,
      template: foundationProject,
      type_of: RecommendationType.FOUNDATIONS,
      target: 1,
    };

    if (updateId) {
      triggerUpdate({
        id: templatePayload.id,
        recommendationTemplate: templatePayload,
      } as any);
    } else {
      triggerInsert({
        id: templatePayload.id,
        recommendationTemplate: templatePayload,
      } as any);
    }

    addToast('Foundation Template Saved', {
      appearance: 'success',
      autoDismiss: true,
      autoDismissTimeout: 1500,
    });
    navigate('/settings/admin/recommendation-templates/foundations');
  };

  const addActivity = (phaseIndex: number) => {
    if (!foundationProject) {
      return;
    }
    let updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities.push({
      activityTitle: '',
      tasks: [],
    });
    setFoundationProject({
      ...foundationProject,
      phases: updatedPhases,
    });
  };
  const removeActivity = (phaseIndex: number, activityIndex: number) => {
    if (!foundationProject) {
      return;
    }
    let updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities = foundationProject.phases[phaseIndex].activities.filter(
      (_: any, i: number) => i !== activityIndex
    );
    setFoundationProject({ ...foundationProject, phases: updatedPhases });
  };

  const addTask = (phaseIndex: number, activityIndex: number) => {
    if (!foundationProject) {
      return;
    }
    const updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].tasks.push({
      task: '',
      metric: '',
      resources: [],
    });
    setFoundationProject({ ...foundationProject, phases: updatedPhases });
  };

  const removeTask = (phaseIndex: number, activityIndex: number, taskIndex: number) => {
    if (!foundationProject) {
      return;
    }
    let updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].tasks = updatedPhases[
      phaseIndex
    ].activities[activityIndex].tasks.filter((_: any, i: number) => i !== taskIndex);
    setFoundationProject({ ...foundationProject, phases: updatedPhases });
  };

  const openResourceModal = (
    phaseIndex: number,
    activityIndex: number,
    taskIndex: number,
    resources: any[]
  ) => {
    // This could be modal props
    setCurrentPhaseIndex(phaseIndex);
    setCurrentactivityIndex(activityIndex);
    setCurrentTaskIndex(taskIndex);
    setCurrentResources(resources);
    setModalOpen(true);
  };

  const addResource = (phaseIndex: number, newResource: any) => {
    if (!foundationProject) {
      return;
    }
    if (currentactivityIndex === null || currentTaskIndex === null) {
      console.warn('Cannot add resource without activity and task index');
      return;
    }
    const updatedResources = [...currentResources, newResource];
    const updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities[currentactivityIndex].tasks[currentTaskIndex]['content'] =
      updatedResources;
    setFoundationProject({ ...foundationProject, phases: updatedPhases });
    setCurrentResources(updatedResources);
  };

  const removeResource = (phaseIndex: number, resourceIndex: number) => {
    if (!foundationProject) {
      return;
    }
    if (currentactivityIndex === null || currentTaskIndex === null) {
      console.warn('Cannot remove resource without activity and task index');
      return;
    }
    const updatedResources = currentResources.filter((_, i) => i !== resourceIndex);
    const updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities[currentactivityIndex].tasks[currentTaskIndex]['content'] =
      updatedResources;
    setFoundationProject({ ...foundationProject, phases: updatedPhases });
    setCurrentResources(updatedResources);
  };

  const updateResourceInfo = (
    phaseIndex: number,
    resourceIndex: number,
    field: any,
    value: any
  ) => {
    if (!foundationProject) {
      return;
    }
    if (currentactivityIndex === null || currentTaskIndex === null) {
      console.warn('Cannot update resource without activity and task index');
      return;
    }
    const updatedResources = [...currentResources];
    updatedResources[resourceIndex][field] = value;
    const updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities[currentactivityIndex].tasks[currentTaskIndex]['content'] =
      updatedResources;
    setFoundationProject({ ...foundationProject, phases: updatedPhases });
    setCurrentResources(updatedResources);
  };

  const updateActivityTitle = (phaseIndex: number, activityIndex: number, value: string) => {
    if (!foundationProject) {
      return;
    }
    const updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].activityTitle = value;
    setFoundationProject({ ...foundationProject, phases: updatedPhases });
  };

  const updateBCorpRelevance = (phaseIndex: number, activityIndex: number, value: string) => {
    if (!foundationProject) {
      return;
    }
    const updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].bCorpRelevance = value;
    setFoundationProject({ ...foundationProject, phases: updatedPhases });
  };

  const updateTaskInfo = (
    phaseIndex: number,
    activityIndex: number,
    taskIndex: number,
    field: string,
    value: string
  ) => {
    if (!foundationProject) {
      return;
    }
    const updatedPhases = [...foundationProject.phases];
    updatedPhases[phaseIndex].activities[activityIndex].tasks[taskIndex][field] = value;
    setFoundationProject({ ...foundationProject, phases: updatedPhases });
  };

  const panes = [
    {
      render: () => (
        <Tab.Pane attached={false}>
          <PhaseTab
            phaseIndex={0}
            addActivity={addActivity}
            phaseData={foundationProject!!.phases[0]}
            updateActivityTitle={updateActivityTitle}
            updateBCorpRelevance={updateBCorpRelevance}
            updateTaskInfo={updateTaskInfo}
            addTask={addTask}
            removeTask={removeTask}
            removeActivity={removeActivity}
            openResourceModal={openResourceModal}
          />
        </Tab.Pane>
      ),
    },
  ];

  function onResourceModalClosed() {
    setModalOpen(false);
  }

  if (projectIsLoading || !foundationProject) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <PageTitle newPageTitle="Foundation Template" />
      <Form>
        <Form>
          <Form.Field>
            <label>Title</label>
            <Input
              value={foundationProject.title}
              onChange={e => setFoundationProject({ ...foundationProject, title: e.target.value })}
            />
          </Form.Field>
          <Form.Field>
            <label>Description</label>
            <TextArea
              value={foundationProject.description}
              onChange={e =>
                setFoundationProject({ ...foundationProject, description: e.target.value })
              }
            />
          </Form.Field>
        </Form>
      </Form>

      <Tab menu={{ secondary: true, pointing: true }} panes={panes} />

      <ResourceModal
        phaseIndex={currentPhaseIndex}
        open={modalOpen}
        onClose={onResourceModalClosed}
        resources={currentResources}
        addResource={addResource}
        removeResource={removeResource}
        updateResourceInfo={updateResourceInfo}
      />

      <SaveContainer>
        <Button onClick={() => navigate('/settings/admin/recommendation-templates/foundations')}>
          Cancel
        </Button>
        <Button primary onClick={saveFoundation}>
          Save Foundation
        </Button>
      </SaveContainer>
    </Container>
  );
};

export default Foundation;

const SaveContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;
